import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["meeting", "readOnly", "required", "value"];
import { Component } from 'react';
import { connect } from 'react-redux';
import { isEqual } from 'underscore';
import { userInfoSync } from 'hub-http/userInfo';
import Routes from 'ContentUtils/Routes';
import FormattedMessage from 'I18n/components/FormattedMessage';
import AsyncSelector from 'ContentComponents/cmv2/components/AsyncSelector';
import UIButton from 'UIComponents/button/UIButton';
import UIFlex from 'UIComponents/layout/UIFlex';
import * as MeetingsActions from 'ContentData/actions/Meetings';
import * as UserActions from 'ContentData/actions/Users';
import { getDefaultMeetingsOptions, getMeeting, getMeetingsOptions, getMeetingsCacheRequestStatus } from 'ContentData/selectors/meetings';
import { getUsers } from 'ContentData/selectors/users';
import { DEFAULT_PAGE_SIZE } from 'ContentComponents/cmv2/constants';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const mapStateToProps = (state, props) => ({
  defaultOptions: getDefaultMeetingsOptions(state),
  meeting: getMeeting(state, {
    link: props.value
  }),
  options: getMeetingsOptions(state),
  optionsRequestStatus: getMeetingsCacheRequestStatus(state),
  users: getUsers(state)
});
const mapDispatchToProps = {
  clearCache: MeetingsActions.clearMeetings,
  fetchMeeting: MeetingsActions.fetchMeeting,
  fetchMeetings: MeetingsActions.fetchMeetings,
  fetchUsers: UserActions.fetchUsersForTeams,
  saveCache: MeetingsActions.saveCache
};
const connector = connect(mapStateToProps, mapDispatchToProps);
const i18nKey = path => `ContentComponents.${path}`;
class MeetingSelect extends Component {
  constructor(props) {
    super(props);
    const {
      user
    } = userInfoSync();
    this.hasPersonalCreateAccess = user.scopes.includes('meetings-personal-link-create');
    this.hasTeamCreateAccess = user.scopes.includes('meetings-team-link-create');
    this.userId = user.user_id;
    this.hasUniversalEditAccess = user.scopes.includes('meetings-edit-all-links');
    this.onChange = this.onChange.bind(this);
    this.handleLoadOptions = this.handleLoadOptions.bind(this);
    this.handleCreateMeeting = this.handleCreateMeeting.bind(this);
    this.handleEditMeeting = this.handleEditMeeting.bind(this);
    this.state = {
      hasMore: true,
      input: ''
    };
  }
  componentDidMount() {
    const {
      fetchMeeting,
      fetchUsers,
      value
    } = this.props;
    if (value && typeof value === 'string') {
      fetchMeeting(value).catch(err => {
        throw err;
      });
    }
    fetchUsers().catch(err => {
      throw err;
    });
  }
  componentDidUpdate(prevProps) {
    const {
      fetchMeeting,
      meeting,
      value
    } = this.props;
    const {
      value: prevValue
    } = prevProps;
    if (value && !isEqual(prevValue, value) && !meeting) {
      fetchMeeting(value).catch(err => {
        throw err;
      });
    }
  }
  onChange({
    target: {
      value
    }
  }) {
    const {
      fetchMeeting,
      onFieldChange
    } = this.props;
    fetchMeeting(value).catch(err => {
      throw err;
    });
    onFieldChange(value);
  }
  handleLoadOptions(input, callback, pagination = {
    after: ''
  }) {
    let after = pagination.after;
    if (input !== this.state.input) {
      this.props.clearCache();
      this.setState({
        hasMore: true,
        input
      });
      after = '';
    }
    const query = {
      after,
      limit: DEFAULT_PAGE_SIZE,
      search: input
    };
    this.props.fetchMeetings(query).then(response => {
      const {
        paging
      } = response;
      const hasMore = paging && paging.next;

      // Has to be a conditional because we only want `hasMore` to reset
      // if the entire meetings cache is cleared.
      if (!hasMore) this.setState({
        hasMore: false
      });
      callback(null, {
        options: this.props.options || [],
        pagination: {
          after: this.state.hasMore && paging.next.after,
          hasMore: this.state.hasMore
        }
      });
    }).catch(err => {
      setTimeout(() => {
        throw err;
      });
    });
  }
  handleCreateMeeting() {
    window.open(Routes.createMeeting());
  }
  handleEditMeeting() {
    const {
      meeting
    } = this.props;
    const {
      id: meetingId,
      portalId
    } = meeting;
    window.open(Routes.editMeeting(portalId, meetingId));
  }
  renderCreateMeetingButton() {
    const {
      hasTeamCreateAccess,
      hasPersonalCreateAccess
    } = this;
    const {
      readOnly
    } = this.props;
    if (hasPersonalCreateAccess || hasTeamCreateAccess) {
      return /*#__PURE__*/_jsx(UIButton, {
        className: "m-left-2",
        size: "xs",
        disabled: readOnly,
        "data-test-id": "create-meeting-button",
        onClick: this.handleCreateMeeting,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('buttons.create')
        })
      });
    }
    return null;
  }
  render() {
    const _this$props = this.props,
      {
        meeting,
        readOnly,
        required,
        value
      } = _this$props,
      rest = _objectWithoutPropertiesLoose(_this$props, _excluded);
    const isEditButtonEnabled = value && meeting && (this.hasUniversalEditAccess || meeting.userId === this.userId);
    return /*#__PURE__*/_jsxs("div", {
      children: [/*#__PURE__*/_jsx(AsyncSelector, Object.assign({
        clearable: !required,
        loadOptions: this.handleLoadOptions,
        onChange: this.onChange,
        readOnly: readOnly,
        value: value
      }, rest)), /*#__PURE__*/_jsxs(UIFlex, {
        className: "m-top-2",
        children: [/*#__PURE__*/_jsx(UIButton, {
          use: "tertiary-light",
          "data-test-id": "edit-meeting-button",
          className: "m-left-0",
          size: "xs",
          disabled: !isEditButtonEnabled,
          onClick: this.handleEditMeeting,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: i18nKey('buttons.edit')
          })
        }), this.renderCreateMeetingButton()]
      })]
    });
  }
}
export default connector(MeetingSelect);